import {gaSessionCookieName, parseGaSessionId} from "./parse-ga-session-id";

function readGaSessionCookie(measurementId: string): string | null {
	// Mirrors `getGaClientIdFromBrowserCookie`'s guard — callers may still be
	// waiting on a timer after the document that scheduled them is gone.
	if (typeof document === "undefined") {
		return null;
	}
	const cookieName = gaSessionCookieName(measurementId);
	const match = document.cookie.match(
		new RegExp(`(?:^|;\\s*)${cookieName}=([^;]+)`)
	);
	if (!match?.[1]) {
		return null;
	}
	return parseGaSessionId(decodeURIComponent(match[1]));
}

/**
 * Resolves once GTM has written the `_ga_<measurementId>` session cookie, or
 * after `maxWaitMs` elapses, whichever comes first.
 *
 * GTM's loader script loads asynchronously (`strategy="beforeInteractive"` +
 * `async=true`, see `GoogleTagManagerScript`), so code that fires a
 * server-tracked event immediately on mount can race ahead of it — the
 * server reads this same cookie to build GA4's `sid` param, and without it
 * GA4 can't tie the hit to any session (see WEL-1954, WEL-2041). This gives
 * the cookie a short window to show up before the caller gives up and fires
 * anyway, so a genuinely slow or blocked GTM never drops the event outright.
 */
export function waitForGaSessionCookie(
	measurementId: string | undefined,
	options: {maxWaitMs?: number; intervalMs?: number} = {}
): Promise<void> {
	const {maxWaitMs = 1500, intervalMs = 50} = options;
	if (!measurementId) {
		return Promise.resolve();
	}
	const confirmedMeasurementId = measurementId;
	return new Promise((resolve) => {
		const deadline = Date.now() + maxWaitMs;
		function poll() {
			if (
				readGaSessionCookie(confirmedMeasurementId) ||
				Date.now() >= deadline
			) {
				resolve();
				return;
			}
			setTimeout(poll, intervalMs);
		}
		poll();
	});
}
